import { Css, Palette, Tooltip, useTestIds } from "@homebound/beam";
import {
  ResponsiveScatterPlot,
  ScatterPlotDatum,
  ScatterPlotRawSerie,
  ScatterPlotSvgProps,
  ScatterPlotTooltipProps,
} from "@nivo/scatterplot";
import { Fragment, useMemo } from "react";

type ScatterPlotProps = {
  dataset: ScatterPlotRawSerie<ScatterPlotDatum>[];
  colors?: Palette[];
  loadingColor?: Palette[];
  disabled?: boolean;
  tooltipValueFormat?: (data: TooltipProps) => string;
  showEmptyStateTooltip?: boolean;
  emptyStateTooltipText?: string;
} & Omit<ScatterPlotSvgProps<ScatterPlotDatum>, "data" | "colors" | "isInteractive" | "height" | "width">;

export function ScatterPlotChart(props: ScatterPlotProps) {
  const { dataset, colors, loadingColor, disabled, tooltipValueFormat, emptyStateTooltipText, ...otherProps } = props;
  const tid = useTestIds({});
  const Chart = useMemo(
    () => (
      <ResponsiveScatterPlot
        data={dataset}
        colors={disabled ? loadingColor : colors}
        isInteractive={!disabled}
        tooltip={tooltipValueFormat ? customTooltip(tooltipValueFormat) : undefined}
        theme={{
          axis: {
            ticks: { text: { fontSize: 10, fill: Palette.Gray700 } },
            legend: { text: { fill: Palette.Gray600 } },
          },
        }}
        {...otherProps}
      />
    ),
    [dataset, colors, loadingColor, disabled, tooltipValueFormat, otherProps],
  );

  return emptyStateTooltipText ? (
    <Tooltip {...tid.dataTooltip} title={emptyStateTooltipText} placement="top" children={Chart} />
  ) : (
    <Fragment children={Chart} />
  );
}

type TooltipProps = ScatterPlotTooltipProps<ScatterPlotDatum & { id?: string }>;

function customTooltip(tooltipValueFormat: (data: TooltipProps) => string) {
  return (data: TooltipProps) => (
    <div css={Css.bgGray900.white.xs.df.mt1.p1.br4.bshBasic.maxwPx(350).mlPx(-250).tal.$}>
      {tooltipValueFormat(data)}
    </div>
  );
}
